/**
 * Austrian-style EU plate badge — canonical across every app.
 *
 * Hoisted from the technical dashboard (where it originated against the
 * mockup `.plate-at` rules). Inline SVG for the EU 12-star ring + the
 * country code "A"; the plate text is plain mono so it stays selectable.
 *
 * Plates are stored raw (no separators) and rendered with Austrian
 * display formatting via `formatPlate` — `S9988BB` shows as `S-9988 BB`,
 * while ambiguous / already-formatted strings pass through untouched
 * (see `formatPlate` for the exact rule). We only add separators where
 * the format is unambiguous, so a plate is never mis-split.
 *
 * Size-dependent pixels are applied as inline styles, NOT Tailwind tokens,
 * so the badge renders identically in every app regardless of that app's
 * Tailwind scale (apps don't all define `h-px26`, `text-12`, … the way the
 * technical app does). Only universal / arbitrary utility classes are used
 * for the rest.
 *
 * Dark mode renders a slate plate instead of a glaring white block — the
 * `dark:` variant resolves to each consuming app's own dark trigger
 * (`class` / `[data-theme=dark]`) when its Tailwind scans this source.
 */
import type { CSSProperties } from 'react'
import { formatPlate } from '../../utils/formatPlate'

export type PlateSize = 'sm' | 'md' | 'lg'

interface PlateProps {
  /**
   * Nullable since backend V0207: imported vehicles and unregistered
   * workshop stock have no plate. Absent renders {@link PlateProps.fallback}
   * rather than an empty plate badge, which reads as a rendering bug.
   */
  readonly text: string | null | undefined
  readonly size?: PlateSize
  readonly className?: string
  /**
   * Give the text a min-width so a column of plates renders to a uniform
   * width (table cells line up). Off by default — inline plates (cards,
   * detail rows) hug their text.
   */
  readonly uniformWidth?: boolean
  /**
   * Shown when `text` is absent. Defaults to the em-dash this codebase
   * already uses for "not recorded", so no new i18n key is needed.
   */
  readonly fallback?: string
}

interface Dims {
  readonly h: number
  readonly bandMinW: number
  readonly bandPadX: number
  readonly bandPadY: number
  readonly star: number
  readonly aFont: number
  readonly textPadX: number
  readonly textFont: number
  /**
   * `uniformWidth` min-width for the text cell, in px. Sized to the widest
   * plate `formatPlate` can emit — a 2-letter district, 5 digits and a
   * 2-letter block renders as `GU-12345 AB`, 11 glyphs — so every real
   * plate lands ON this floor and a column comes out flush.
   *
   * px, not `ch`: `ch` measures a glyph advance and ignores both the
   * 0.6px letter-spacing and (under border-box) the horizontal padding,
   * which is why the previous `8.5ch` was quietly under every plate in
   * the table and never applied. Each value = 11 glyph advances (0.6em)
   * + 11 letter-spacings + both paddings, rounded up.
   */
  readonly textMinW: number
}

// sm + lg are the technical dashboard's mockup-exact values; md is
// interpolated at h=28 so management tables (which used the old shared
// plate's `md`) keep their row height.
const SIZES: Record<PlateSize, Dims> = {
  sm: { h: 26, bandMinW: 22, bandPadX: 4, bandPadY: 2, star: 13, aFont: 10, textPadX: 9, textFont: 12, textMinW: 106 },
  md: { h: 28, bandMinW: 23, bandPadX: 4, bandPadY: 3, star: 14, aFont: 11, textPadX: 10, textFont: 13, textMinW: 115 },
  lg: { h: 36, bandMinW: 28, bandPadX: 5, bandPadY: 3, star: 18, aFont: 13, textPadX: 13, textFont: 15, textMinW: 134 },
}

// 12 stars on a clock ring, drawn as dots (not glyphs) so they stay crisp
// at plate sizes. Coordinates live in a 0..16 viewBox.
const STAR_POINTS = [
  { cx: 8, cy: 2 },
  { cx: 11, cy: 2.8 },
  { cx: 13.2, cy: 5 },
  { cx: 14, cy: 8 },
  { cx: 13.2, cy: 11 },
  { cx: 11, cy: 13.2 },
  { cx: 8, cy: 14 },
  { cx: 5, cy: 13.2 },
  { cx: 2.8, cy: 11 },
  { cx: 2, cy: 8 },
  { cx: 2.8, cy: 5 },
  { cx: 5, cy: 2.8 },
] as const

export function Plate({
  text,
  size = 'sm',
  className = '',
  uniformWidth = false,
  fallback = '\u2014',
}: PlateProps) {
  const d = SIZES[size]
  const display = formatPlate(text)
  // No plate to draw. A plate badge framing an em-dash looks like a real
  // plate that failed to load, so render plain muted text instead — the
  // row still lines up, and it reads as "not recorded" rather than broken.
  if (!display) {
    return (
      <span
        className={['inline-flex items-center align-middle opacity-50', className].join(' ')}
        // A real plate is the EU band (min width + both paddings) plus the
        // text block, so matching only textMinW left placeholder cells
        // narrower than their neighbours and ragged the column.
        style={{
          height: d.h,
          minWidth: uniformWidth ? d.bandMinW + d.bandPadX * 2 + d.textMinW : undefined,
          justifyContent: uniformWidth ? 'center' : undefined,
        }}
        data-testid="plate-empty"
      >
        {fallback}
      </span>
    )
  }
  const textStyle: CSSProperties = {
    paddingLeft: d.textPadX,
    paddingRight: d.textPadX,
    fontSize: d.textFont,
    letterSpacing: '0.6px', // mockup `.plate-text`
  }
  if (uniformWidth) textStyle.minWidth = d.textMinW
  return (
    <span
      className={[
        'inline-flex items-stretch overflow-hidden bg-white align-middle leading-none font-mono flex-shrink-0',
        // 1.5px border + 5px radius come from mockup `.plate-at` — odd pixel
        // values, so arbitrary bracket utilities are the discoverable option.
        'border-[1.5px] border-[#1a1a1a] rounded-[5px]',
        // Dark mode: a sleek slate plate instead of a glaring white block.
        'dark:bg-[#272b34] dark:border-white/15',
        className,
      ].join(' ')}
      style={{ height: d.h }}
      data-testid="plate"
      title={display}
    >
      <span
        className="flex flex-col items-center justify-evenly bg-[#003399]"
        style={{
          minWidth: d.bandMinW,
          paddingLeft: d.bandPadX,
          paddingRight: d.bandPadX,
          paddingTop: d.bandPadY,
          paddingBottom: d.bandPadY,
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" className="block" style={{ height: d.star, width: d.star }}>
          <g fill="#FFCC00">
            {STAR_POINTS.map((pt, i) => (
              <circle key={i} cx={pt.cx} cy={pt.cy} r={0.85} />
            ))}
          </g>
        </svg>
        <span className="font-sans font-extrabold leading-none text-white" style={{ fontSize: d.aFont }}>
          A
        </span>
      </span>
      <span
        className="flex flex-1 items-center justify-center whitespace-nowrap font-black text-black dark:text-white"
        style={textStyle}
      >
        {display}
      </span>
    </span>
  )
}
